import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { mitigationContentsCategorySchema, mitigationContentsConditionSchema, mitigationContentsHandlingStatusSchema, } from '../contracts/mitigation-contents-inventory.contract.js';
export const mitigationContentsConfigurationValueSchema = z.strictObject({
    panelEnabled: z.boolean(),
    defaultCategory: mitigationContentsCategorySchema,
    defaultCondition: mitigationContentsConditionSchema,
    defaultHandlingStatus: mitigationContentsHandlingStatusSchema,
    defaultAnnualDepreciationBasisPoints: z.number().int().min(0).max(10_000),
    currency: z.string().trim().regex(/^[A-Z]{3}$/),
    maximumItemsPerProject: z.number().int().min(1).max(100_000),
    maximumBulkItems: z.number().int().min(1).max(200),
});
export const mitigationContentsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'mitigation.contents.configuration',
        moduleId: 'mitigation',
        title: 'Mitigation customer-contents configuration',
        helpText: 'Controls customer-belongings inventory defaults and limits. This is separate from Assets warehouse inventory.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelEnabled: true,
            defaultCategory: 'general',
            defaultCondition: 'good',
            defaultHandlingStatus: 'documented',
            defaultAnnualDepreciationBasisPoints: 1_000,
            currency: 'USD',
            maximumItemsPerProject: 10_000,
            maximumBulkItems: 200,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: mitigationContentsConfigurationValueSchema,
});
export const mitigationContentsDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100), z.literal(250)]),
    defaultGroupBy: z.enum(['room', 'category', 'handling-status']),
    showVoidedItems: z.boolean(),
    showCostsWhenPermitted: z.boolean(),
    showHistory: z.boolean(),
});
export const mitigationContentsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.contents.display',
        moduleId: 'mitigation',
        title: 'Mitigation customer-contents display',
        helpText: 'Controls grouping, density, card size, history, void visibility, and permitted cost visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            density: 'comfortable',
            cardSize: 'standard',
            pageSize: 100,
            defaultGroupBy: 'room',
            showVoidedItems: false,
            showCostsWhenPermitted: true,
            showHistory: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationContentsDisplayValueSchema,
});
const label = z.string().trim().min(1).max(160);
const message = z.string().trim().min(1).max(500);
export const mitigationContentsCopyValueSchema = z.strictObject({
    panelTitle: label,
    panelDescription: message,
    separationNotice: message,
    searchLabel: label,
    roomFilterLabel: label,
    categoryFilterLabel: label,
    statusFilterLabel: label,
    allLabel: label,
    groupByLabel: label,
    groupByRoomLabel: label,
    groupByCategoryLabel: label,
    groupByStatusLabel: label,
    addItemAction: label,
    editItemAction: label,
    saveItemAction: label,
    voidItemAction: label,
    exportAction: label,
    roomLabel: label,
    itemNameLabel: label,
    descriptionLabel: label,
    categoryLabel: label,
    quantityLabel: label,
    ageMonthsLabel: label,
    conditionLabel: label,
    handlingStatusLabel: label,
    locationLabel: label,
    replacementUnitCostLabel: label,
    annualDepreciationLabel: label,
    replacementTotalLabel: label,
    depreciationTotalLabel: label,
    actualCashValueLabel: label,
    evidenceLabel: label,
    notesLabel: label,
    reasonLabel: label,
    historyLabel: label,
    revisionLabel: label,
    voidedLabel: label,
    loadingLabel: label,
    emptyLabel: message,
    loadError: message,
    mutationError: message,
    costsHiddenLabel: message,
    categories: z.record(mitigationContentsCategorySchema, label),
    conditions: z.record(mitigationContentsConditionSchema, label),
    handlingStatuses: z.record(mitigationContentsHandlingStatusSchema, label),
});
export const mitigationContentsCopySetting = definePlatformSetting({
    definition: {
        key: 'mitigation.copy.contents',
        moduleId: 'mitigation',
        title: 'Mitigation customer-contents copy',
        helpText: 'Controls tenant-editable labels for customer belongings, valuation, handling status, and retained history.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Customer contents',
            panelDescription: 'Document customer belongings, condition, valuation, evidence, and handling status without erasing prior versions.',
            separationNotice: 'Customer belongings are kept here. Company tools and warehouse materials remain in the optional Assets module.',
            searchLabel: 'Search contents',
            roomFilterLabel: 'Room',
            categoryFilterLabel: 'Category',
            statusFilterLabel: 'Handling status',
            allLabel: 'All',
            groupByLabel: 'Group by',
            groupByRoomLabel: 'Room',
            groupByCategoryLabel: 'Category',
            groupByStatusLabel: 'Handling status',
            addItemAction: 'Add contents item',
            editItemAction: 'Edit current item',
            saveItemAction: 'Save retained version',
            voidItemAction: 'Void item',
            exportAction: 'Export contents CSV',
            roomLabel: 'Room',
            itemNameLabel: 'Item name',
            descriptionLabel: 'Description',
            categoryLabel: 'Category',
            quantityLabel: 'Quantity',
            ageMonthsLabel: 'Age in months',
            conditionLabel: 'Condition',
            handlingStatusLabel: 'Handling status',
            locationLabel: 'Current location',
            replacementUnitCostLabel: 'Replacement cost each',
            annualDepreciationLabel: 'Annual depreciation %',
            replacementTotalLabel: 'Replacement total',
            depreciationTotalLabel: 'Depreciation total',
            actualCashValueLabel: 'Actual cash value',
            evidenceLabel: 'Evidence references',
            notesLabel: 'Notes',
            reasonLabel: 'Change reason',
            historyLabel: 'Retained version history',
            revisionLabel: 'Revision',
            voidedLabel: 'Voided',
            loadingLabel: 'Loading customer contents…',
            emptyLabel: 'No customer contents match the current filters.',
            loadError: 'Customer contents could not be loaded.',
            mutationError: 'The contents change could not be saved. Refresh and try again.',
            costsHiddenLabel: 'Valuation is hidden by your permission.',
            categories: {
                furniture: 'Furniture', electronics: 'Electronics', appliances: 'Appliances',
                clothing: 'Clothing', documents: 'Documents', tools: 'Customer tools',
                sports: 'Sports', toys: 'Toys', art: 'Art', jewelry: 'Jewelry',
                general: 'General', other: 'Other',
            },
            conditions: { excellent: 'Excellent', good: 'Good', fair: 'Fair', poor: 'Poor' },
            handlingStatuses: {
                documented: 'Documented', 'packed-out': 'Packed out', cleaning: 'Cleaning',
                stored: 'Stored', returned: 'Returned', disposed: 'Disposed',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: mitigationContentsCopyValueSchema,
});
export const mitigationContentsSettingRegistrations = [
    mitigationContentsConfigurationSetting,
    mitigationContentsDisplaySetting,
    mitigationContentsCopySetting,
];
//# sourceMappingURL=mitigation-contents-inventory-setting-definitions.js.map